import type { TranslationKey } from '@myosh/odin-translations/i18next';
import type { Editor } from '@tiptap/react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowDownToLine,
  ArrowLeftToLine,
  ArrowRightToLine,
  ArrowUpToLine,
  Columns3,
  Heading,
  TableCellsMerge,
  TableCellsSplit,
  Trash2,
} from 'lucide-react';
import type { ComponentType } from 'react';

/** Cell horizontal alignment. Mirrors the values accepted by TipTap's built-in cell `align`
 * attribute, which serialises to an inline `text-align` style. */
export type TableCellAlign = 'left' | 'center' | 'right';

/** Visual grouping for separator placement in the menu. */
export type TableActionGroup = 'insert' | 'header' | 'merge' | 'align' | 'delete';

export interface TableAction {
  id: string;
  group: TableActionGroup;
  labelKey: TranslationKey;
  /** Optional dynamic label key (e.g. toggle vs. remove header). Takes precedence over `labelKey`. */
  resolveLabelKey?: (editor: Editor) => TranslationKey;
  icon: ComponentType;
  run: (editor: Editor) => void;
  variant?: 'default' | 'destructive';
  /** Whether the action applies to the current selection. Defaults to "the cursor is in a table". */
  isAvailable?: (editor: Editor) => boolean;
  /** Whether the action reflects the current state (e.g. the active alignment). */
  isActive?: (editor: Editor) => boolean;
}

const inTable = (editor: Editor) => editor.isActive('table');

/** True when the cell (data or header) under the selection has the given alignment applied. */
export const isCellAlignActive = (editor: Editor, align: TableCellAlign): boolean =>
  editor.getAttributes('tableCell').align === align || editor.getAttributes('tableHeader').align === align;

/** Set the cell alignment, or pass `null` to clear it (falls back to the default left). Works on a
 * single cell or a multi-cell CellSelection, so aligning a drag-selection aligns every cell at once. */
export const setCellAlign = (editor: Editor, align: TableCellAlign | null): boolean =>
  editor.chain().focus().setCellAttribute('align', align).run();

const ALIGN_ICONS: Record<TableCellAlign, ComponentType> = {
  left: AlignLeft,
  center: AlignCenter,
  right: AlignRight,
};

const ALIGN_LABEL_KEYS: Record<TableCellAlign, TranslationKey> = {
  left: 'table-align-left',
  center: 'table-align-centre',
  right: 'table-align-right',
};

const alignAction = (align: TableCellAlign): TableAction => ({
  id: `align-${align}`,
  group: 'align',
  labelKey: ALIGN_LABEL_KEYS[align],
  icon: ALIGN_ICONS[align],
  isActive: (e) => isCellAlignActive(e, align),
  // Toggle: re-selecting the active alignment clears it.
  run: (e) => {
    setCellAlign(e, isCellAlignActive(e, align) ? null : align);
  },
});

export const TABLE_ACTIONS: TableAction[] = [
  {
    id: 'add-row-before',
    group: 'insert',
    labelKey: 'table-insert-row-above',
    icon: ArrowUpToLine,
    run: (e) => {
      e.chain().focus().addRowBefore().run();
    },
  },
  {
    id: 'add-row-after',
    group: 'insert',
    labelKey: 'table-insert-row-below',
    icon: ArrowDownToLine,
    run: (e) => {
      e.chain().focus().addRowAfter().run();
    },
  },
  {
    id: 'add-column-before',
    group: 'insert',
    labelKey: 'table-insert-column-left',
    icon: ArrowLeftToLine,
    run: (e) => {
      e.chain().focus().addColumnBefore().run();
    },
  },
  {
    id: 'add-column-after',
    group: 'insert',
    labelKey: 'table-insert-column-right',
    icon: ArrowRightToLine,
    run: (e) => {
      e.chain().focus().addColumnAfter().run();
    },
  },
  {
    id: 'toggle-header-row',
    group: 'header',
    labelKey: 'table-toggle-header-row',
    icon: Heading,
    run: (e) => {
      e.chain().focus().toggleHeaderRow().run();
    },
  },
  {
    id: 'toggle-header-column',
    group: 'header',
    labelKey: 'table-toggle-header-column',
    icon: Columns3,
    run: (e) => {
      e.chain().focus().toggleHeaderColumn().run();
    },
  },
  {
    id: 'merge-cells',
    group: 'merge',
    labelKey: 'table-merge-cells',
    icon: TableCellsMerge,
    isAvailable: (e) => inTable(e) && e.can().mergeCells(),
    run: (e) => {
      e.chain().focus().mergeCells().run();
    },
  },
  {
    id: 'split-cell',
    group: 'merge',
    labelKey: 'table-split-cell',
    icon: TableCellsSplit,
    isAvailable: (e) => inTable(e) && e.can().splitCell(),
    run: (e) => {
      e.chain().focus().splitCell().run();
    },
  },
  alignAction('left'),
  alignAction('center'),
  alignAction('right'),
  {
    id: 'delete-row',
    group: 'delete',
    variant: 'destructive',
    labelKey: 'table-delete-row',
    icon: Trash2,
    run: (e) => {
      e.chain().focus().deleteRow().run();
    },
  },
  {
    id: 'delete-column',
    group: 'delete',
    variant: 'destructive',
    labelKey: 'table-delete-column',
    icon: Trash2,
    run: (e) => {
      e.chain().focus().deleteColumn().run();
    },
  },
  {
    id: 'delete-table',
    group: 'delete',
    variant: 'destructive',
    labelKey: 'table-delete-table',
    icon: Trash2,
    run: (e) => {
      e.chain().focus().deleteTable().run();
    },
  },
];

/** Whether an action should be shown/enabled for the current selection. */
export const isTableActionAvailable = (action: TableAction, editor: Editor): boolean =>
  (action.isAvailable ?? inTable)(editor);

export const TABLE_ACTIONS_BY_ID: ReadonlyMap<string, TableAction> = new Map(TABLE_ACTIONS.map((a) => [a.id, a]));
